import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import { Box, ClickAwayListener, IconButton } from "@mui/material";
import { memo, useState } from "react";
import CustomEmojiPicker from "./CustomEmojiPicker";

interface EmojiPopoverProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
}

/**
 * @description Emoji picker toggled inline (no MUI Popover/Modal). A Popover
 * traps and restores focus, which kept stealing focus back from the chat input;
 * rendering the picker as a plain absolutely-positioned element avoids that
 * entirely, so the input keeps/regains focus without any timing race.
 * @param value
 * @param onChange
 *
 * @author Rajkumar Rathod
 */
const EmojiPopover = <T extends string>({
  value,
  onChange,
}: EmojiPopoverProps<T>) => {
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <IconButton size="medium" onClick={() => setOpen((prev) => !prev)}>
          <SentimentSatisfiedAltIcon />
        </IconButton>

        {open && (
          <Box
            sx={{
              position: "absolute",
              bottom: "calc(100% + 8px)",
              right: 0,
              zIndex: (theme) => theme.zIndex.modal,
            }}
          >
            <CustomEmojiPicker
              /**
               * @description since search is not clickable issue form library
               * @link [https://www.npmjs.com/package/emoji-picker-react]
               */
              searchDisabled
              skinTonesDisabled
              value={value}
              onChange={(v) => {
                onChange(v);
                setOpen(false);
              }}
            />
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default memo(EmojiPopover);
